import React from "react";
import "../../../css/ViewQuestionsModal.css";

// Interface for a full question object
export interface FullQuestion {
  questionId: string;
  text: string;
  answerType: "Radio" | "Checkbox" | "Text" | "FileUpload" | "Number" | "Single-choice" | string;
  answerOptions?: string[];
  conditionalDisplayValue?: string;
  questionGroup?: string;
  subsectionTitle: string;
}

interface ViewQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  version: number;
  setTitle: string;
  totalQuestions: number;
  createdOn: string;
  questions: FullQuestion[];
}

const QuestionItem: React.FC<{ question: FullQuestion, index: number }> = ({ question, index }) => {
  const getIcon = () => {
    switch (question.answerType) {
      case "Text":
        return <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>;
      case "Single-choice":
      case "Radio":
        return <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="4"></circle></svg>;
      case "Checkbox":
        return <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>;
      case "FileUpload":
          return <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;
      case "Number":
        return <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"></line><line x1="4" y1="15" x2="20" y2="15"></line><line x1="10" y1="3" x2="8" y2="21"></line><line x1="16" y1="3" x2="14" y2="21"></line></svg>;
      default:
        return <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;
    }
  };

  const renderAnswerArea = () => {
    switch (question.answerType) {
      case "Single-choice":
      case "Radio":
        return ( <div className="modern-options-list"> {question.answerOptions?.map((opt, i) => ( <div key={i} className="modern-option-item"> <div className="modern-radio"></div> <span className="modern-option-label">{opt}</span> </div> ))} </div> );
      case "Checkbox":
        return ( <div className="modern-options-list"> {question.answerOptions?.map((opt, i) => ( <div key={i} className="modern-option-item"> <div className="modern-checkbox"></div> <span className="modern-option-label">{opt}</span> </div> ))} </div> );
      case "Text":
        return <textarea className="modern-mock-textarea" placeholder="User response text area..." readOnly />;
      case "FileUpload":
        return (
          <div className="modern-mock-upload-box">
             <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
             <span>Drop files here to upload</span>
          </div>
        );
      case "Number":
        return <input type="number" className="modern-mock-input" placeholder="Numeric response..." readOnly />;
      default:
        return <p className="unsupported-type">Unsupported question type</p>;
    }
  };

  return (
    <div className="modern-question-card" style={{ animationDelay: `${index * 0.05}s` }}>
      <div className="modern-question-header">
        <div className="modern-question-icon-wrapper">
          {getIcon()}
        </div>
        <div className="modern-question-title-wrapper">
          <h3 className="modern-question-title">{question.text}</h3>
          <div className="modern-question-badges">
            <span className="modern-badge-type">{question.answerType}</span>
            {question.conditionalDisplayValue && <span className="modern-badge-conditional">Conditional</span>}
          </div>
        </div>
      </div>
      <div className="modern-question-body">
        {renderAnswerArea()}
      </div>
    </div>
  );
};

export const ViewQuestionsModal: React.FC<ViewQuestionsModalProps> = ({ isOpen, onClose, version, setTitle, totalQuestions, createdOn, questions }) => {
  if (!isOpen) return null;

  return (
    <div className="modern-modal-overlay" onClick={onClose}>
      <div className="modern-modal-drawer" onClick={(e) => e.stopPropagation()}>
        
        <div className="modern-drawer-header">
          <div className="modern-header-title-section">
            <div className="modern-header-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
            </div>
            <h2 className="modern-drawer-title">Questionnaire Preview</h2>
          </div>
          <button className="modern-drawer-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div className="modern-drawer-info-grid">
          <div className="modern-info-card">
            <span className="modern-info-label">Version</span>
            <span className="modern-info-value">v{version}.0</span>
          </div>
          <div className="modern-info-card">
            <span className="modern-info-label">Set Title</span>
            <span className="modern-info-value" title={setTitle}>{setTitle}</span>
          </div>
          <div className="modern-info-card">
            <span className="modern-info-label">Total Questions</span>
            <span className="modern-info-value">{totalQuestions}</span>
          </div>
          <div className="modern-info-card">
            <span className="modern-info-label">Created On</span>
            <span className="modern-info-value">{new Date(createdOn).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="modern-drawer-body">
          <div className="modern-questions-list">
            {questions.map((q, idx) => (
              <QuestionItem key={q.questionId} question={q} index={idx} />
            ))}
          </div>
        </div>

        <div className="modern-drawer-footer">
          <button className="modern-btn-primary" onClick={onClose}>Done</button>
        </div>

      </div>
    </div>
  );
};